import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  InputGroup,
  Card,
} from "react-bootstrap";
import { BiUpload } from "react-icons/bi";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";
import { capitalizeFirstLetter } from "../../utils/Helper";

const AddSubadmin = () => {
  const { id } = useParams();
  const location = useLocation();
  const { row } = location.state || {};
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { accessToken, logout } = useContext(UserContext);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    mobile: "",
    profile_pic: null,
    password: "",
    confirmPassword: "",
    role_id: "",
    country_code: "",
    country_id: "",
  });
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [imagePreview, setImagePreview] = useState(row?.profile_pic || null);
  const navigate = useNavigate();
  const FileInputRef = useRef(null);
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const headers = {
    "content-type": "multipart/form-data",
    "auth-token": authToken,
    authorization: accessToken,
  };
  const [errors, setErrors] = useState({});

useEffect(() => {
  fetchRole();
  fetchCountriesCode();
  if (id && row) {
    const [firstName, ...lastNameParts] = row?.fullname.split(" ");
    const lastName = lastNameParts.join(" ");
    setFormData({
      firstName,
      lastName,
      email: row?.email || "",
      mobile: row?.mobile || "",
      profile_pic: null,
      username: row?.username || "",
      role_id: row?.role_id || "",
      country_code: row?.country_code || "",
      country_id: row?.country_id?.toString() || "", // Convert to string for select comparison
      password: "",
      confirmPassword: "",
    });
    setImagePreview(row?.profile_pic || null);
  }
}, [id, row]);
  const fetchRole = async () => {
    try {
      const response = await APIServices.get(
        `/admin/role_list?status=Active&limit=100&system_role=0`,
        headers
      );
      if (response?.status === 200) {
        const data = response?.data?.data?.list || [];
        setCategories(data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchCountriesCode = async () => {
    try {
      const response = await APIServices.get(
        `/admin/cms/country_list`,
        headers
      );
      if (response?.status === 200 && response?.data?.status === true) {
        const countriesList = response?.data?.data?.countries || [];
        setCountries(countriesList);
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([]);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = "First name is required.";
    }

    if (!formData.lastName) {
      newErrors.lastName = "Last name is required.";
    }
    if (!formData.username) {
      newErrors.username = "Username is required.";
    }

    if (!formData.email) {
      newErrors.email = "Email address is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid.";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Phone number is invalid.";
    }

    if (!id) {
      // Only validate password fields if adding new user
      if (!formData.password) {
        newErrors.password = "Password is required.";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters.";
      } else if (formData.password.length > 20) {
        newErrors.password = "Password must not exceed 20 characters.";
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Confirm password is required.";
      } else if (formData.confirmPassword !== formData.password) {
        newErrors.confirmPassword = "Passwords do not match.";
      }
    }

    if (!formData.role_id) {
      newErrors.role_id = "Role is required.";
    } else if (
      !categories.some((category) => category.id == formData.role_id)
    ) {
      newErrors.role_id = "Invalid role selected.";
    }

    // Profile pic is NOT mandatory, so no error if empty.
    if (formData.profile_pic && formData.profile_pic instanceof File) {
      if (
        !["image/jpeg", "image/png", "image/gif"].includes(
          formData.profile_pic.type
        )
      ) {
        newErrors.profile_pic = "Profile picture must be a JPG, PNG, or GIF";
      }
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_pic") {
      setFormData({ ...formData, [name]: files[0] || null });
    } else if (name === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData({
        ...formData,
        [name]: numericValue,
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setErrors({ ...errors, [name]: "" });
  };

  const onImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevState) => ({
        ...prevState,
        profile_pic: file,
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
      setFormData((prevState) => ({
        ...prevState,
        profile_pic: null,
      }));
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const validationErrors = validate();

  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  setErrors({});

  const { firstName, lastName, confirmPassword, ...rest } = formData;

  const updatedFormData = {
    ...rest,
    fullname: `${firstName} ${lastName}`.trim(),
    username: formData.username,
    // Both country_id and country_code are already in formData
  };

  // Prepare form data to submit (multipart/form-data)
  const formDataToSubmit = new FormData();

  Object.entries(updatedFormData).forEach(([key, value]) => {
    if (key === "profile_pic") {
      if (value instanceof File) {
        formDataToSubmit.append(key, value);
      }
    } else {
      formDataToSubmit.append(key, value);
    }
  });

  try {
    if (id) {
      formDataToSubmit.append("id", id);
      const res = await APIServices.post(
        `/admin/update_admin`,
        formDataToSubmit,
        { "content-type": "multipart/form-data", ...headers }
      );
      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Success", "success");
        setTimeout(() => {
          navigate("/admin/sub-admin");
        }, 2000);
      } else if (res?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    } else {
      const res = await APIServices.post(
        "/admin/add_admin",
        formDataToSubmit,
        headers
      );
      if (res?.data?.status === true) {
        showToast(res?.data?.message || "Success", "success");
        setTimeout(() => {
          navigate("/admin/sub-admin");
        }, 2000);
      } else {
        showToast(res?.data?.message || "Something went wrong.", "error");
      }
    }
  } catch (error) {
    console.error("API call error:", error);
    showToast("An unexpected error occurred. Please try again.", "error");
  }
};
  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  return (
    <>
      <div className="add_channel_sec add_member_sec">
        <div className="addmom_header d-flex justify-content-start mb-3">
          <h1 className="font_blue_18 font-600">
            {id ? "Edit Sub-Admin" : "Add Sub-Admin"}
          </h1>
        </div>

        <Card className="add_channel_form">
          <Card.Body>
            <Form className="">
              <Row>
                <Col md={12} lg={12}>
                  <div className="d-flex flex-column ">
                    <label htmlFor="upload-input" className="upload-circle">
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Uploaded"
                          className="uploaded-img"
                        />
                      ) : (
                        <BiUpload size={30} color="#333" />
                      )}
                    </label>
                    <Form.Control
                      type="file"
                      name="profile_pic"
                      id="upload-input"
                      className="d-none"
                      accept="image/jpeg,image/png,image/gif"
                      onChange={(e) => {
                        onImageChange(e);
                        handleChange(e);
                      }}
                      ref={FileInputRef}
                      isInvalid={!!errors.profile_pic}
                    />
                    <p className="mt-2 font_blue_14">Profile Pic</p>
                    {errors.profile_pic && (
                      <div className="text-danger">{errors.profile_pic}</div>
                    )}
                  </div>
                </Col>

                <Col md={4} lg={4}>
                  <Form.Group controlId="firstName">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      First Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      placeholder="Enter First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                    {errors.firstName && (
                      <div className="text-danger">{errors.firstName}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} lg={4}>
                  <Form.Group controlId="lastName">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      Last Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      placeholder="Enter Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                    {errors.lastName && (
                      <div className="text-danger">{errors.lastName}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} lg={4}>
                  <Form.Group controlId="username">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      Username
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      placeholder="Enter Username"
                      value={formData.username}
                      onChange={handleChange}
                    />
                    {errors.username && (
                      <div className="text-danger">{errors.username}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} lg={4}>
                  <Form.Group controlId="emailId">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      Email ID
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter Email ID"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <div className="text-danger">{errors.email}</div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4} lg={4}>
                  <Form.Group controlId="phoneNo">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      Phone No
                    </Form.Label>
                    <InputGroup>
                      <Form.Select
                        name="country_id" // Change to country_id
                        value={formData.country_id}
                        onChange={(e) => {
                          const selectedCountry = countries.find(
                            country => country.id.toString() === e.target.value
                          );
                          setFormData({
                            ...formData,
                            country_id: e.target.value,
                            country_code: selectedCountry?.phonecode || ""
                          });
                        }}
                        style={{ maxWidth: "200px", borderWidth: "0px" }}
                      >
                        <option value="">Select Code</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name} ({country.phonecode})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        name="mobile"
                        placeholder="Enter Phone No"
                        maxLength="10"
                        value={formData.mobile}
                        onChange={handleChange}
                      />
                    </InputGroup>
                    {errors.mobile && (
                      <div className="text-danger">{errors.mobile}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} lg={4}>
                  <Form.Group controlId="country">
                    <Form.Label className="font_blue_14 font-500 mb-2 ">
                      Role
                    </Form.Label>
                    <Form.Select
                      name="role_id"
                      value={formData.role_id}
                      onChange={handleChange}
                    >
                      <option value="">Select Role</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {capitalizeFirstLetter(category.name)}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.role_id && (
                      <div className="text-danger">{errors.role_id}</div>
                    )}
                  </Form.Group>
                </Col>

                {!id && (
                  <>
                    <Col md={4} lg={4}>
                      <Form.Group className="mb-3" controlId="formPassword">
                        <Form.Label className="font_blue_14 font-500 mb-2">
                          Password
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Enter Password"
                            value={formData.password}
                            onChange={handleChange}
                            isInvalid={!!errors.password}
                          />
                          <InputGroup.Text
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: "pointer" }}
                          >
                            {showPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                          </InputGroup.Text>
                        </InputGroup>
                        {errors.password && (
                          <div className="text-danger">{errors.password}</div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={4} lg={4}>
                      <Form.Group className="mb-3" controlId="formConfirmPassword">
                        <Form.Label className="font_blue_14 font-500 mb-2">
                          Confirm Password
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            isInvalid={!!errors.confirmPassword}
                          />
                          <InputGroup.Text
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            {showConfirmPassword ? <FaRegEye /> : <FaRegEyeSlash />}
                          </InputGroup.Text>
                        </InputGroup>
                        {errors.confirmPassword && (
                          <div className="text-danger">{errors.confirmPassword}</div>
                        )}
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>
              <div className="text-center mt-3">
                <Button variant="primary" type="submit" onClick={handleSubmit}>
                  {id ? "Update" : "Submit"}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <ToastContainer />
    </>
  );
};

export default AddSubadmin;
